import { useState, useEffect, useCallback } from "react";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { AppSidebar } from "@/components/app-sidebar"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import DashboardHome from './components/DashboardHome';
import ImageConverterPage from './components/ImageConverterPage';
import VideoConverterPage from './components/VideoConverterPage';
import DocumentConverterPage from './components/DocumentConverterPage';
import { CopywriterAgentPage } from './components/CopywriterAgentPage';
import { useImageResizer } from './hooks/useImageResizer';
import { useVideoConverter } from './hooks/useVideoConverter';
import { useDocumentConverter } from './hooks/useDocumentConverter';
import { usePageLoading } from './hooks/usePageLoading';
import { PageLoading, RouteLoading } from './components/ui/loading';
import { PageType } from './types';
import ToastContainer from '@/components/ui/toast-container';

type ThemeMode = 'light' | 'dark' | 'auto';

const navigationItems = [
  {
    id: 'home' as const,
    title: 'Dashboard',
    icon: 'Home',
    description: 'Visão geral e início',
    path: '/'
  },
  {
    id: 'images' as const,
    title: 'Conversor de Imagens',
    icon: 'ImageIcon',
    description: 'Redimensionar e converter imagens',
    path: '/images'
  },
  {
    id: 'videos' as const,
    title: 'Conversor de Vídeos',
    icon: 'Video',
    description: 'Converter formatos de vídeo',
    path: '/videos'
  },
  {
    id: 'documents' as const,
    title: 'Conversor de Documentos',
    icon: 'FileText',
    description: 'Converter documentos PDF, Word, etc.',
    path: '/documents'
  },
  {
    id: 'copywriter' as const,
    title: 'Agente CopyWriter',
    icon: 'PenTool',
    description: 'Gere descrições profissionais para produtos',
    path: '/copywriter'
  }
];

export default function App() {
  const [themeMode, setThemeMode] = useState<ThemeMode>('auto');
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Usar React Router para navegação
  const location = useLocation();
  const navigate = useNavigate();

  // Função utilitária para aplicar transições ULTRA RÁPIDAS
  const applyThemeTransition = (callback: () => void) => {
    // Usar requestAnimationFrame para sincronização otimizada
    requestAnimationFrame(() => {
      document.documentElement.classList.add('theme-transitioning');
      
      // Aplicar mudanças usando React.flushSync para atualizações síncronas
      requestAnimationFrame(() => {
        callback();
        
        // Remover classe de transição com timing suavizado
        setTimeout(() => {
          document.documentElement.classList.remove('theme-transitioning');
        }, 200);
      });
    });
  };

  // Função OTIMIZADA para aplicar cores personalizadas do ConverterPro
  const applyConverterProTheme = (isDark: boolean) => {
    const root = document.documentElement;
    
    // Verificar se o estado atual já é o desejado para evitar transições desnecessárias
    const currentIsDark = root.classList.contains('dark');
    if (currentIsDark === isDark) return;
    
    // Batch de mudanças CSS para melhor performance
    const cssText = isDark ? `
      --background: #2A2E3F;
      --foreground: var(--cp-text-primary);
      --card: #383D52;
      --card-foreground: var(--cp-text-primary);
      --popover: #383D52;
      --popover-foreground: var(--cp-text-primary);
      --primary: var(--cp-accent);
      --primary-foreground: #ffffff;
      --secondary: var(--cp-bg-tertiary);
      --secondary-foreground: var(--cp-text-secondary);
      --muted: var(--cp-bg-tertiary);
      --muted-foreground: var(--cp-text-muted);
      --accent: var(--cp-bg-tertiary);
      --accent-foreground: var(--cp-text-secondary);
      --destructive: var(--cp-error);
      --destructive-foreground: #ffffff;
      --border: var(--cp-border);
      --input: var(--cp-border);
      --ring: var(--cp-accent);
    ` : `
      --background: var(--cp-bg-primary);
      --foreground: var(--cp-text-primary);
      --card: var(--cp-bg-tertiary);
      --card-foreground: var(--cp-text-primary);
      --popover: var(--cp-bg-tertiary);
      --popover-foreground: var(--cp-text-primary);
      --primary: var(--cp-accent);
      --primary-foreground: #ffffff;
      --secondary: var(--cp-bg-secondary);
      --secondary-foreground: #ffffff;
      --muted: var(--cp-bg-secondary);
      --muted-foreground: var(--cp-text-muted);
      --accent: var(--cp-bg-secondary);
      --accent-foreground: #ffffff;
      --destructive: var(--cp-error);
      --destructive-foreground: #ffffff;
      --border: var(--cp-border);
      --input: var(--cp-border);
      --ring: var(--cp-accent);
    `;
    
    // Aplicar todas as mudanças CSS de uma vez
    root.style.cssText += cssText;
  };

  // Hook para gerenciar loading de páginas
  const { isLoading, isRouteChanging } = usePageLoading();

  // Determinar página atual baseada na rota
  const getCurrentPageFromPath = (pathname: string): PageType => {
    switch (pathname) {
      case '/images':
        return 'images';
      case '/videos':
        return 'videos';
      case '/documents':
        return 'documents';
      case '/copywriter':
        return 'copywriter';
      default:
        return 'home';
    }
  };

  const currentPage = getCurrentPageFromPath(location.pathname);

  const handlePageChange = (newPage: PageType) => {
    console.log(`App: Mudando página de '${currentPage}' para '${newPage}'`);
    console.log(`App: Location atual:`, location);
    const targetPath = navigationItems.find(item => item.id === newPage)?.path || '/';
    console.log(`App: Navegando para path:`, targetPath);
    navigate(targetPath);
  };
  
  const {
    images,
    settings,
    isProcessing,
    handleFileSelect,
    processImage,
    processImages,
    downloadImage,
    downloadAll,
    downloadAllAsZip,
    removeImage,
    clearAll,
    onUpdateSettings
  } = useImageResizer();

  const {
    videos,
    settings: videoSettings,
    isProcessing: isVideoProcessing,
    handleFileSelect: handleVideoFileSelect,
    processVideo,
    processVideos,
    downloadVideo,
    downloadAll: downloadAllVideos,
    downloadAllAsZip: downloadAllVideosAsZip,
    removeVideo,
    clearAll: clearAllVideos,
    updateSettings: updateVideoSettings
  } = useVideoConverter();

  const {
    documents,
    settings: documentSettings,
    isProcessing: isDocumentProcessing,
    handleFileSelect: handleDocumentFileSelect,
    processDocument,
    processDocuments,
    downloadDocument,
    downloadAll: downloadAllDocuments,
    downloadAllAsZip: downloadAllDocumentsAsZip,
    removeDocument,
    clearAll: clearAllDocuments,
    updateSettings: updateDocumentSettings
  } = useDocumentConverter();

  const handleProcessAll = useCallback(() => {
    const pendingImages = images.filter(img => img.status === 'pending');
    processImages(pendingImages);
  }, [images, processImages]);

  // Monitor para debug da navegação
  useEffect(() => {
    console.log(`🔄 Página atual alterada para: ${currentPage} (path: ${location.pathname})`);
  }, [currentPage, location.pathname]);

  // Detectar preferência do sistema e inicializar tema
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme-mode') as ThemeMode || 'auto';
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    setThemeMode(savedTheme);
    
    const shouldBeDark = savedTheme === 'dark' || (savedTheme === 'auto' && systemPrefersDark);
    setIsDarkMode(shouldBeDark);
    
    // Aplicar classe e cores personalizadas imediatamente
    document.documentElement.classList.toggle('dark', shouldBeDark);
    applyConverterProTheme(shouldBeDark);
  }, []);

  // Escutar mudanças na preferência do sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = () => {
      if (themeMode === 'auto') {
        applyThemeTransition(() => {
          setIsDarkMode(mediaQuery.matches);
          document.documentElement.classList.toggle('dark', mediaQuery.matches);
          applyConverterProTheme(mediaQuery.matches);
        });
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [themeMode]);

  const setTheme = (mode: ThemeMode) => {
    applyThemeTransition(() => {
      setThemeMode(mode);
      localStorage.setItem('theme-mode', mode);
      
      let shouldBeDark: boolean;
      
      switch (mode) {
        case 'dark':
          shouldBeDark = true;
          break;
        case 'light':
          shouldBeDark = false;
          break;
        case 'auto':
        default:
          shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          break;
      }
      
      setIsDarkMode(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
      applyConverterProTheme(shouldBeDark);
    });
  };

  const getCurrentPageInfo = () => {
    return navigationItems.find(item => item.id === currentPage) || navigationItems[0];
  };

  const currentPageInfo = getCurrentPageInfo();

  const getBreadcrumbs = () => {
    if (currentPage === 'home') {
      return (
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbPage>Dashboard</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      );
    }

    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem className="hidden md:block">
            <BreadcrumbLink 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                handlePageChange('home');
              }}
            >
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="hidden md:block" />
          <BreadcrumbItem>
            <BreadcrumbPage>{currentPageInfo.title}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  };

  return (
    <SidebarProvider>
      {/* Loading para carregamento inicial da página */}
      {isLoading && <PageLoading />}
      
      {/* Loading para transições de rota */}
      {isRouteChanging && <RouteLoading />}
      
      <AppSidebar 
        currentPage={currentPage}
        themeMode={themeMode}
        onThemeChange={setTheme}
        isDarkMode={isDarkMode}
      />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-4"
            />
            {getBreadcrumbs()}
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <div className="min-h-[100vh] flex-1 rounded-xl md:min-h-min">
            <Routes>
              <Route 
                path="/" 
                element={
                  <DashboardHome 
                    isDarkMode={isDarkMode} 
                    onPageChange={handlePageChange}
                  />
                } 
              />
              <Route 
                path="/images" 
                element={
                  <ImageConverterPage 
                    images={images}
                    settings={settings}
                    isProcessing={isProcessing}
                    onFileSelect={handleFileSelect}
                    onProcessImage={processImage}
                    onProcessAll={handleProcessAll}
                    onDownloadImage={downloadImage}
                    onDownloadAll={downloadAll}
                    onDownloadAllAsZip={downloadAllAsZip}
                    onRemoveImage={removeImage}
                    onClearAll={clearAll}
                    onUpdateSettings={onUpdateSettings}
                    isDarkMode={isDarkMode}
                  />
                } 
              />
              <Route 
                path="/videos" 
                element={
                  <VideoConverterPage 
                    videos={videos}
                    settings={videoSettings}
                    isProcessing={isVideoProcessing}
                    onFileSelect={handleVideoFileSelect}
                    onProcessVideo={processVideo}
                    onProcessAll={processVideos}
                    onDownloadVideo={downloadVideo}
                    onDownloadAll={downloadAllVideos}
                    onDownloadAllAsZip={downloadAllVideosAsZip}
                    onRemoveVideo={removeVideo}
                    onClearAll={clearAllVideos}
                    onUpdateSettings={updateVideoSettings}
                    isDarkMode={isDarkMode}
                  />
                } 
              />
              <Route 
                path="/documents" 
                element={
                  <DocumentConverterPage 
                    documents={documents}
                    settings={documentSettings}
                    isProcessing={isDocumentProcessing}
                    onFileSelect={handleDocumentFileSelect}
                    onProcessDocument={processDocument}
                    onProcessAll={processDocuments}
                    onDownloadDocument={downloadDocument}
                    onDownloadAll={downloadAllDocuments}
                    onDownloadAllAsZip={downloadAllDocumentsAsZip}
                    onRemoveDocument={removeDocument}
                    onClearAll={clearAllDocuments}
                    onUpdateSettings={updateDocumentSettings}
                    isDarkMode={isDarkMode}
                  />
                } 
              />
              <Route 
                path="/copywriter" 
                element={
                  <CopywriterAgentPage />
                } 
              />
              <Route 
                path="*" 
                element={<Navigate to="/" replace />}
              />
            </Routes>
          </div>
        </div>
      </SidebarInset>
      <ToastContainer />
    </SidebarProvider>
  );
}
